import React, { useEffect, useState } from 'react';
import { Plus, PlusCircle, Layers, Trash2, BookOpen, User, Clock, Pencil } from 'lucide-react';
import AddKnowledgeBaseModal from './AddKnowledgeBaseModal';
import KnowledgebaseManagementModal from './KnowledgebaseManagementModal';
import KnowledgeEntityModal from './KnowledgeEntityModal';
import { ingestionService } from '../../../../components/api/ingestionService';
import { getUserInfo } from '../../../../routes/decodedAdmin';
import ConfirmDialog from '../../Dialogs/ConfirmDialog';
import RenameModal from '../../common/RenameModal';
import { knowledgebaseService } from '../../../../components/api/knowledgebaseService';
interface Entity {
    id: string;
    name: string;
}

interface KnowledgeWorkspace {
    id: string;
    tenantId: string;
    title: string;
    description: string;
    entities: Entity[];
    createdBy: string;
    createdOn: string;
}

const DataSourceListingPage: React.FC = () => {
    const [search, setSearch] = useState('');
    const [workspaces, setWorkspaces] = useState<KnowledgeWorkspace[]>([]);
    const [showEntityModal, setShowEntityModal] = useState(false);
    const [selectedWorkspace, setSelectedWorkspace] = useState<KnowledgeWorkspace | null>(null);
    const [showKBModal, setShowKBModal] = useState(false);
    const [workspaceToDelete, setWorkspaceToDelete] = useState<KnowledgeWorkspace | null>(null);

    useEffect(() => {

        fetchWorkspaces();
    }, []);
    const fetchWorkspaces = async () => {
        try {
            const response = await ingestionService.loadkbEntitiesList();
            setWorkspaces(response);
        } catch (err) {
            console.error('Failed to fetch workspaces', err);
        }
    };
    const filtered = workspaces.filter(w =>
        w.title.toLowerCase().includes(search.toLowerCase())
    );

    const handleDelete = async () => {
        const token = localStorage.getItem('token'); // or whatever key you use
        if (token) {
            const userInfo = getUserInfo(token);
            try {
                if (userInfo) {
                    await ingestionService.deleteKBEntityGroup(workspaceToDelete?.id, userInfo.sub);
                    setWorkspaceToDelete(null);
                    fetchWorkspaces();
                }
            } catch (err) {
                console.error('Failed to delete workspace', err);
                alert('Failed to delete workspace.');
            }
        }
        if (!workspaceToDelete) return;

    };


    const handleManage = (workspace: KnowledgeWorkspace) => {
        setSelectedWorkspace(workspace);
        setShowKBModal(true);
    };

    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renameTarget, setRenameTarget] = useState<{ id: string; name: string } | null>(null);


    return (
        <div className="px-6 py-8">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h2 className="text-xl font-semibold text-white">Available Knowledge Workspaces</h2>
                    <p className="text-sm text-gray-400">Group, manage, and configure your entities here.</p>
                </div>
                <button
                    onClick={() => setShowEntityModal(true)}
                    className="flex items-center gap-1 bg-indigo-600 hover:bg-indigo-500 px-3 py-1.5 rounded text-[11px] font-medium shadow text-white"
                >
                    <Plus className="w-4 h-4" /> Add New
                </button>
            </div>

            {/* Search */}
            <div className="mb-6">
                <input
                    type="text"
                    placeholder="Search workspaces..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full px-4 py-2 rounded-md bg-neutral-800 text-white placeholder-gray-400 focus:outline-none text-sm"
                />
            </div>

            {/* Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {filtered.map((workspace) => (
                    <div
                        key={workspace.id}
                        className="bg-neutral-900 border border-neutral-800 rounded-lg p-5 hover:border-indigo-500 transition-all text-sm"
                    >
                        <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <BookOpen className="w-5 h-5 text-indigo-400" />
                                <h3 className="text-white text-base font-medium">{workspace.title}</h3>
                            </div>
                        </div>
                        <p className="text-xs text-gray-400 mb-2">{workspace.description}</p>

                        <div className="text-gray-400 text-xs mb-2 space-y-2">
                            <div className="flex items-center gap-2">
                                <User className="w-3.5 h-3.5 text-gray-500" />
                                <span>{workspace.createdBy || "Unknown"}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="w-3.5 h-3.5 text-gray-500" />
                                <span>
                                    {new Date(workspace.createdOn).getFullYear() > 2000
                                        ? new Date(workspace.createdOn).toLocaleString()
                                        : "Unknown"}
                                </span>
                            </div>
                        </div>
                        <div className="text-gray-400 text-xs mb-2 space-y-2">
                            <div className="flex items-center gap-2">
                                <Layers className="w-3.5 h-3.5 text-gray-500" />
                                <span>
                                    {workspace.entities?.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mb-2">
                                            {workspace.entities.map((entity) => (
                                                <span
                                                    key={entity.id}
                                                    className="inline-block bg-neutral-800 text-gray-300 px-2 py-0.5 rounded-full text-[11px] font-medium hover:bg-indigo-800/40 transition cursor-default max-w-xs truncate"
                                                    title={entity.name}
                                                >
                                                    {entity.name.length > 30 ? entity.name.slice(0, 30) + '…' : entity.name}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </span>
                            </div>
                        </div>



                        {/* Manage Button */}
                        <div className="flex justify-end gap-2 mt-4">
                            <button
                                onClick={() => handleManage(workspace)}
                                className="text-indigo-400 hover:text-indigo-300 text-xs flex items-center"
                            >
                                <PlusCircle className="w-4 h-4 mr-1" /> Manage
                            </button>
                            <button onClick={() => {
                                setRenameTarget({ id: workspace.id, name: workspace.title });
                                setShowRenameModal(true);
                            }} className="text-yellow-400 hover:text-yellow-300" title="Edit Source">
                                <Pencil className="w-4 h-4" />
                            </button>
                            <button
                                className="text-red-400 hover:text-red-300"
                                onClick={() => setWorkspaceToDelete(workspace)}
                                title="Delete Workspace"
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>

                        </div>
                    </div>
                ))}
                {filtered.length === 0 && (
                    <div className="col-span-full text-center text-gray-500 py-8">No workspaces found.</div>
                )}
            </div>

            {/* Modals */}
            {showEntityModal && (
                <KnowledgeEntityModal
                    tenantId="2f4e558c-15e5-4246-965d-c107088b1ddb"
                    userId="2f4e558c-15e5-4246-965d-c107088b1ddb"
                    onClose={() => setShowEntityModal(false)}
                    onSubmit={(data) => {
                        fetchWorkspaces()
                    }}
                />
            )}

            {showKBModal && selectedWorkspace && (
                <KnowledgebaseManagementModal
                    workspaceid={selectedWorkspace.id}
                    onClose={() => {
                        setShowKBModal(false);
                        setSelectedWorkspace(null);
                    }}
                />
            )}

            {workspaceToDelete != null && (
                <ConfirmDialog
                    message={`Are you sure you want to delete "${workspaceToDelete.title}"?`}
                    onYes={handleDelete}
                    onCancel={() => setWorkspaceToDelete(null)}
                />
            )}


            {renameTarget && showRenameModal && (
                <RenameModal
                    id={renameTarget.id}
                    currentName={renameTarget.name}
                    module="Knowledge Workspace"
                    onCancel={() => {
                        setShowRenameModal(false);
                        setRenameTarget(null);
                    }}
                    onSave={({ id, newName }) => {
                        knowledgebaseService.renameSource({
                            knowledgeBaseId: id,
                            tenantID: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
                            newTitle: newName,
                        }).then(() => {
                            fetchWorkspaces();
                            setShowRenameModal(false);
                            setRenameTarget(null);
                        });
                    }}

                />
            )}


        </div>
    );
};

export default DataSourceListingPage;
